import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { TileProps } from "@app/shared/interfaces/dashboard";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import Roles from "@app/user/roles"
import RoleRestricted from "@app/user/RoleRestricted";
import * as React from "react";
import { Link } from "react-router-dom";

function getTiles(featureFlags: Set<string>): TileProps[] {
  return [
    {
      allowedRoles: [Roles.OPERATIONS_ADMIN],
      icon: QesIcons.TerminalBrowser,
      route: "jobs",
      title: "Jobs",
    },
    {
      allowedRoles: [Roles.OPERATIONS_ADMIN],
      description: "Map",
      icon: QesIcons.MapV1,
      route: "jobqueuemap",
      title: "Job Queue Map",
    },
    {
      allowedRoles: [Roles.OPERATIONS_ADMIN],
      icon: QesIcons.LayersThreeV1,
      route: "jobqueues",
      title: "Job Queues",
    },
    {
      allowedRoles: [Roles.OPERATIONS_ADMIN],
      icon: QesIcons.ClockRewind,
      route: "weekendrunmonitoring",
      title: "Weekend Run Monitoring",
    },
    {
      allowedRoles: [Roles.OPERATIONS_ADMIN],
      icon: QesIcons.Quinn,
      route: "airesponsereview",
      title: "AI Response Review"
    },
    {
      allowedRoles: [Roles.OPERATIONS_ADMIN],
      icon: QesIcons.RefreshCcwV2,
      route: "providerhubingestionretry",
      title: "Provider Hub Ingestion Retry",
    },
    {
      allowedRoles: [Roles.OPERATIONS_ADMIN, Roles.OPERATIONS_USER],
      icon: QesIcons.BarChartV7,
      route: "job-performance",
      title: "Job Performance",
    },
    {
      allowedRoles: [
        Roles.SNOWFLAKE_DATA_SHARING_ADMIN,
        Roles.SNOWFLAKE_DATA_SHARING_VIEWER,
      ],
      icon: QesIcons.ShareV6,
      route: "snowflakedatasharing",
      title: "Snowflake Data Sharing",
    },
    {
      allowedRoles: [Roles.OPERATIONS_ADMIN, Roles.OPERATIONS_USER],
      icon: QesIcons.PresentationChartV1,
      route: "project-performance",
      title: "Project Performance",
    },
    ...(featureFlags.has(Features.MARKET_NIGHTLY_REFRESH) ? [{
      allowedRoles: [Roles.OPERATIONS_ADMIN],
      icon: QesIcons.RefreshCcwV2,
      requiredFeatureFlag: Features.MARKET_NIGHTLY_REFRESH,
      route: "marketnightlyrefresh",
      title: "Market Nightly Refresh",
    }] : [])
  ]
};

export const OperationsDashboard: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const featureFlags = React.useContext(FeatureFlagContext).enabledFeatureFlags;

  return (
    <Page documentTitle="Operations Dashboard">
      <Breadcrumbs
        template={BreadcrumbTemplateEnum.Operations}
        items={["Dashboard"]}
      />
      <PageInner>
        <PageHeader title="Operations Dashboard" disableHR={false} />
        <PageSection>
          <div className="container mt-3">
            {getTiles(featureFlags).map((tile) => (
              <Tile
                key={tile.title}
                allowedRoles={tile.allowedRoles}
                description={tile.description}
                icon={tile.icon}
                isV1={tile.isV1}
                route={tile.route}
                title={tile.title}
              />
            ))}
          </div>
        </PageSection>
      </PageInner>
    </Page>
  );
};

const Tile: React.FC<React.PropsWithChildren<TileProps>> = ({
  allowedRoles,
  icon,
  description,
  route = "#",
  title,
  isV1,
}) => {
  if (isV1) {
    return (
      <RoleRestricted allowedRoles={allowedRoles}>
        <a
          href={`${window.uiEndpoint}/${route}`}
          className="dashboard-tile col-4"
        >
          {icon !== "" && (
            <QesIcon
              className="dashboard-tile__qes-icon"
              description={description}
              iconName={icon}
              isDecorative={true}
            />
          )}
          <h6>{title}</h6>
        </a>
      </RoleRestricted>
    );
  } else {
    return (
      <RoleRestricted allowedRoles={allowedRoles}>
        <Link to={route} className="dashboard-tile col-4">
          {icon !== "" && (
            <QesIcon
              className="dashboard-tile__qes-icon"
              description={description}
              iconName={icon}
            />
          )}
          <h6>{title}</h6>
        </Link>
      </RoleRestricted>
    );
  }
};
